import { useQuery } from '@tanstack/react-query'
import { getHealth } from '../api/client'
import type { HealthResponse } from '../api/types'

/**
 * A permanent, small read of `GET /health`.
 *
 * It is here because the two ways this app can be wrong are both invisible from a swap card.
 * If WP3's refresher stops, quotes start failing with `POOL_STATE_STALE` and every pair
 * looks unroutable; if the circuit breaker trips, quotes get quietly coarser. Both are
 * stated on `/health` in so many words, and both would otherwise be diagnosed by guessing.
 */
function tone(health: HealthResponse | undefined, isError: boolean): string {
  if (isError || !health) return 'bad'
  if (health.status === 'ok') return 'good'
  if (health.status === 'degraded') return 'warn'
  return 'bad'
}

export function HealthStrip() {
  const { data, isError } = useQuery<HealthResponse, Error>({
    queryKey: ['health'],
    queryFn: ({ signal }) => getHealth(signal),
    refetchInterval: 30_000,
    retry: false,
  })

  const state = tone(data, isError)
  const poolAge = data?.poolState.oldestSeconds

  return (
    <div className={`health health-${state}`} title={data ? JSON.stringify(data.poolState, null, 2) : undefined}>
      <span className="health-dot" />
      <span className="health-label">{isError || !data ? 'API unreachable' : `API ${data.status}`}</span>
      {data && (
        <>
          <span className="health-sep">·</span>
          <span>
            pool data {poolAge === null || poolAge === undefined ? 'unknown' : `${Math.round(poolAge)}s old`}
            {data.poolState.stalePools > 0 && ` (${data.poolState.stalePools} stale)`}
          </span>
          {data.breaker.state !== 'closed' && (
            <>
              <span className="health-sep">·</span>
              <span>breaker {data.breaker.state}</span>
            </>
          )}
        </>
      )}
    </div>
  )
}
